import React from 'react';
import NavBar from '../components/Navbar/NavBar';
import Footer from '../components/Footer';
import { useDocTitle } from '../components/CustomHook';
import { Link } from 'react-router-dom';
import img4 from '../images/consultation.svg';

const ProcessImprovement = () => {
    useDocTitle('Process Improvement - Claravio Solutions');

    return (
        <>
            <NavBar />
            <div className="bg-gray-50 min-h-screen pt-28">
                {/* Hero Section */}
                <div className="bg-claravio-blue-900 text-white py-20">
                    <div className="container mx-auto px-4 text-center">
                        <h1 className="text-4xl lg:text-6xl font-bold mb-4">Process Improvement</h1>
                        <p className="text-xl lg:text-2xl mb-8">Transform operational distractions into improvement opportunities</p>
                        <Link to="/contact" className="bg-white text-claravio-blue-900 px-8 py-3 rounded-lg font-semibold hover:bg-gray-100 transition duration-300">
                            Start Optimizing
                        </Link>
                    </div>
                </div>

                {/* Main Content */}
                <div className="container mx-auto px-4 py-16">
                    <div className="grid lg:grid-cols-2 gap-12 items-center mb-16">
                        <div>
                            <h2 className="text-3xl font-bold text-claravio-blue-900 mb-6">Optimize Your Operations</h2>
                            <p className="text-lg text-gray-700 mb-6">
                                Our process optimization framework allows you to quickly identify gaps (opportunities) 
                                between current state operations and future state goals. We help you eliminate operational 
                                distractions and quickly turn improvement opportunities into results.
                            </p>
                            <p className="text-lg text-gray-700">
                                Every organization has hidden inefficiencies that drain resources and slow progress. 
                                Our systematic approach uncovers these bottlenecks and transforms them into competitive advantages.
                            </p>
                        </div>
                        <div>
                            <img src={img4} alt="Process Improvement Consulting" className="w-full max-w-md mx-auto" />
                        </div>
                    </div>

                    {/* Framework Section */}
                    <div className="mb-16">
                        <h2 className="text-3xl font-bold text-claravio-blue-900 text-center mb-12">Our Optimization Framework</h2>
                        <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-8">
                            <div className="bg-white p-6 rounded-lg shadow-lg text-center">
                                <div className="bg-claravio-blue-900 text-white w-16 h-16 rounded-full flex items-center justify-center mx-auto mb-4">
                                    <svg xmlns="http://www.w3.org/2000/svg" width="32" height="32" viewBox="0 0 24 24" fill="currentColor">
                                        <path d="M12 2C6.48 2 2 6.48 2 12s4.48 10 10 10 10-4.48 10-10S17.52 2 12 2zm-2 15l-5-5 1.41-1.41L10 14.17l7.59-7.59L19 8l-9 9z"/>
                                    </svg>
                                </div>
                                <h3 className="text-xl font-semibold text-claravio-blue-900 mb-4">Assess</h3>
                                <p className="text-gray-600">Analyze current state operations and identify improvement opportunities</p>
                            </div>
                            <div className="bg-white p-6 rounded-lg shadow-lg text-center">
                                <div className="bg-claravio-blue-900 text-white w-16 h-16 rounded-full flex items-center justify-center mx-auto mb-4">
                                    <svg xmlns="http://www.w3.org/2000/svg" width="32" height="32" viewBox="0 0 24 24" fill="currentColor">
                                        <path d="M12 2l3.09 6.26L22 9.27l-5 4.87 1.18 6.88L12 17.77l-6.18 3.25L7 14.14 2 9.27l6.91-1.01L12 2z"/>
                                    </svg>
                                </div>
                                <h3 className="text-xl font-semibold text-claravio-blue-900 mb-4">Design</h3>
                                <p className="text-gray-600">Create future state vision and detailed improvement roadmap</p>
                            </div>
                            <div className="bg-white p-6 rounded-lg shadow-lg text-center">
                                <div className="bg-claravio-blue-900 text-white w-16 h-16 rounded-full flex items-center justify-center mx-auto mb-4">
                                    <svg xmlns="http://www.w3.org/2000/svg" width="32" height="32" viewBox="0 0 24 24" fill="currentColor">
                                        <path d="M12 2C6.48 2 2 6.48 2 12s4.48 10 10 10 10-4.48 10-10S17.52 2 12 2zm5 11h-4v4h-2v-4H7v-2h4V7h2v4h4v2z"/>
                                    </svg>
                                </div>
                                <h3 className="text-xl font-semibold text-claravio-blue-900 mb-4">Implement</h3>
                                <p className="text-gray-600">Execute improvements with minimal disruption to daily operations</p>
                            </div>
                            <div className="bg-white p-6 rounded-lg shadow-lg text-center">
                                <div className="bg-claravio-blue-900 text-white w-16 h-16 rounded-full flex items-center justify-center mx-auto mb-4">
                                    <svg xmlns="http://www.w3.org/2000/svg" width="32" height="32" viewBox="0 0 24 24" fill="currentColor">
                                        <path d="M16 6l2.29 2.29-4.88 4.88-4-4L2 16.59 3.41 18l6-6 4 4 6.3-6.29L22 12V6h-6z"/>
                                    </svg>
                                </div>
                                <h3 className="text-xl font-semibold text-claravio-blue-900 mb-4">Measure</h3>
                                <p className="text-gray-600">Track results and continuously optimize for sustained improvement</p>
                            </div>
                        </div>
                    </div>

                    {/* Services Section */}
                    <div className="mb-16">
                        <h2 className="text-3xl font-bold text-claravio-blue-900 text-center mb-12">Process Improvement Services</h2>
                        <div className="grid md:grid-cols-2 gap-8">
                            <div className="bg-white p-8 rounded-lg shadow-lg">
                                <h3 className="text-2xl font-semibold text-claravio-blue-900 mb-4">Operational Assessment</h3>
                                <ul className="space-y-3 text-gray-700">
                                    <li className="flex items-start"><span className="text-claravio-blue-900 mr-2">•</span> Current state analysis</li>
                                    <li className="flex items-start"><span className="text-claravio-blue-900 mr-2">•</span> Bottleneck identification</li>
                                    <li className="flex items-start"><span className="text-claravio-blue-900 mr-2">•</span> Resource utilization review</li>
                                    <li className="flex items-start"><span className="text-claravio-blue-900 mr-2">•</span> Performance gap analysis</li>
                                </ul>
                            </div>
                            <div className="bg-white p-8 rounded-lg shadow-lg">
                                <h3 className="text-2xl font-semibold text-claravio-blue-900 mb-4">Process Redesign</h3>
                                <ul className="space-y-3 text-gray-700">
                                    <li className="flex items-start"><span className="text-claravio-blue-900 mr-2">•</span> Workflow optimization</li>
                                    <li className="flex items-start"><span className="text-claravio-blue-900 mr-2">•</span> Standard operating procedures</li>
                                    <li className="flex items-start"><span className="text-claravio-blue-900 mr-2">•</span> Quality control systems</li>
                                    <li className="flex items-start"><span className="text-claravio-blue-900 mr-2">•</span> Performance metrics design</li>
                                </ul>
                            </div>
                            <div className="bg-white p-8 rounded-lg shadow-lg">
                                <h3 className="text-2xl font-semibold text-claravio-blue-900 mb-4">Change Management</h3>
                                <ul className="space-y-3 text-gray-700">
                                    <li className="flex items-start"><span className="text-claravio-blue-900 mr-2">•</span> Stakeholder engagement</li>
                                    <li className="flex items-start"><span className="text-claravio-blue-900 mr-2">•</span> Training and development</li>
                                    <li className="flex items-start"><span className="text-claravio-blue-900 mr-2">•</span> Communication strategies</li>
                                    <li className="flex items-start"><span className="text-claravio-blue-900 mr-2">•</span> Adoption monitoring</li>
                                </ul>
                            </div>
                            <div className="bg-white p-8 rounded-lg shadow-lg">
                                <h3 className="text-2xl font-semibold text-claravio-blue-900 mb-4">Continuous Improvement</h3>
                                <ul className="space-y-3 text-gray-700">
                                    <li className="flex items-start"><span className="text-claravio-blue-900 mr-2">•</span> Performance monitoring</li>
                                    <li className="flex items-start"><span className="text-claravio-blue-900 mr-2">•</span> Regular reviews and updates</li>
                                    <li className="flex items-start"><span className="text-claravio-blue-900 mr-2">•</span> Best practice implementation</li>
                                    <li className="flex items-start"><span className="text-claravio-blue-900 mr-2">•</span> Ongoing optimization</li>
                                </ul>
                            </div>
                        </div>
                    </div>

                    {/* Results Section 
                    <div className="bg-white rounded-lg p-8 shadow-lg mb-16">
                        <h2 className="text-3xl font-bold text-claravio-blue-900 text-center mb-12">Typical Results</h2>
                        <div className="grid md:grid-cols-3 gap-8 text-center">
                            <div>
                                <div className="text-4xl font-bold text-claravio-blue-900 mb-2">25-40%</div>
                                <p className="text-gray-600">Reduction in process cycle time</p>
                            </div>
                            <div>
                                <div className="text-4xl font-bold text-claravio-blue-900 mb-2">15-30%</div>
                                <p className="text-gray-600">Cost savings through efficiency gains</p>
                            </div>
                            <div>
                                <div className="text-4xl font-bold text-claravio-blue-900 mb-2">50-75%</div>
                                <p className="text-gray-600">Reduction in process errors</p>
                            </div>
                        </div>
                    </div> */}

                    {/* CTA Section */}
                    <div className="text-center bg-claravio-blue-900 text-white p-12 rounded-lg">
                        <h2 className="text-3xl font-bold mb-4">Ready to Optimize Your Processes?</h2>
                        <p className="text-xl mb-8">Discover hidden efficiencies and transform your operations today.</p>
                        <Link to="/contact" className="bg-white text-claravio-blue-900 px-8 py-3 rounded-lg font-semibold hover:bg-gray-100 transition duration-300">
                            Get Your Assessment
                        </Link>
                    </div>
                </div>
            </div>
            <Footer />
        </>
    );
};

export default ProcessImprovement;